// Brand Console DATA -- Paid Media.
//
// Consumes ONLY the real connector -> intelligence_bridge ->
// intelligence_measurements pipeline (connectors/google_ads.py,
// connectors/paid_media.py, connectors/intelligence_bridge.py's own
// AD_METRIC_SPECS) via the existing GET
// /intelligence/{workspace_id}/measurements route (source_type=CONNECTOR)
// and the existing GET /connectors/{workspace_id} route for connection
// state. Deliberately does NOT read the legacy /ads/* routes
// (AdsPage.js's own data source) -- those are confirmed fabricated
// (server.py's random.randint/random.uniform literals), so they are
// never wired into this page.
//
// Real metric_key vocabulary a campaign-entity ad measurement can carry
// (intelligence_bridge.py's own AD_METRIC_SPECS, verified directly, not
// assumed): spend, impressions, clicks, ctr, cpc, conversions,
// conversion_value, roas, cpa. Summary CTR/CPC/ROAS below are RECOMPUTED
// from the summed absolute metrics (clicks/impressions/spend/
// conversion_value) rather than averaged from the provider's own
// per-observation ratio records -- summing a ratio across many rows
// would itself be a subtly wrong number, not an honest one.

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { DashboardLayout } from '@/components/DashboardLayout';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { StatusBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { measurementsAPI, connectorsAPI, entitiesAPI } from '@/lib/intelligenceApi';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';
import { useNavigate } from 'react-router-dom';

const AD_PROVIDERS = ['google_ads', 'meta_marketing'];
const PROVIDER_LABELS = { google_ads: 'Google Ads', meta_marketing: 'Meta Ads' };

function providerOf(measurement) {
  return (measurement.source_reference || '').split(':')[0];
}

function sum(records, key) {
  return records.filter((r) => r.metric_key === key).reduce((acc, r) => acc + (r.value || 0), 0);
}

function formatNumber(n, digits = 0) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

export default function PaidMediaPage() {
  const navigate = useNavigate();
  // P1.9: client resolution is now the shared useWorkspaceSelection() hook
  // (see SignalsPage.js's own comment for the full rationale) instead of
  // an unswitchable first-client fetch.
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const clientId = workspaceId || null;
  const noClient = !loadingClients && clients.length === 0;
  const [connectors, setConnectors] = useState(null); // null = loading
  const [measurements, setMeasurements] = useState(null); // null = loading
  const [entityNames, setEntityNames] = useState({});
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState('observed_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);

  const load = useCallback(() => {
    if (!clientId) return;
    setConnectors(null); setMeasurements(null); setError(null);
    Promise.all([
      connectorsAPI.list(clientId),
      measurementsAPI.listForWorkspace(clientId, { source_type: 'CONNECTOR' }),
      entitiesAPI.list(clientId, { entityType: 'campaign' }).catch(() => ({ data: { entities: [] } })),
    ])
      .then(([connRes, measRes, entRes]) => {
        setConnectors(connRes.data?.connectors || []);
        const adRecords = (measRes.data?.measurements || []).filter((m) => AD_PROVIDERS.includes(providerOf(m)));
        setMeasurements(adRecords);
        const names = {};
        (entRes.data?.entities || entRes.data || []).forEach((e) => { names[e.id] = e.canonical_name || e.id; });
        setEntityNames(names);
      })
      .catch((e) => setError(e));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const connectedProviders = useMemo(
    () => (connectors || []).filter((c) => AD_PROVIDERS.includes(c.provider) && c.status === 'CONNECTED'),
    [connectors],
  );
  const isConnected = connectedProviders.length > 0;

  // Final completeness audit finding: an INVALIDATED measurement is the
  // backend's own explicit "this record is no longer trustworthy" state
  // (MeasurementService.invalidate_measurement) -- summing it into a
  // real-looking total would silently misrepresent real data as valid.
  // The raw table below still shows every record, INVALIDATED included
  // (with its real status visible), for full transparency; only the
  // aggregated Summary/Trends/Breakdown views exclude it.
  const validMeasurements = useMemo(
    () => (measurements || []).filter((m) => m.status !== 'INVALIDATED'),
    [measurements],
  );

  const summary = useMemo(() => {
    if (!measurements) return null;
    const spend = sum(validMeasurements, 'spend');
    const impressions = sum(validMeasurements, 'impressions');
    const clicks = sum(validMeasurements, 'clicks');
    const conversions = sum(validMeasurements, 'conversions');
    const revenue = sum(validMeasurements, 'conversion_value');
    return {
      spend, impressions, clicks, conversions, revenue,
      ctr: impressions > 0 ? (clicks / impressions) * 100 : null,
      cpc: clicks > 0 ? spend / clicks : null,
      roas: spend > 0 ? revenue / spend : null,
    };
  }, [measurements, validMeasurements]);

  const trend = useMemo(() => {
    if (!measurements) return [];
    const byDate = {};
    validMeasurements.forEach((m) => {
      const date = (m.observed_at || '').slice(0, 10);
      if (!date) return;
      byDate[date] = byDate[date] || { date, spend: 0, revenue: 0, conversions: 0 };
      if (m.metric_key === 'spend') byDate[date].spend += m.value || 0;
      if (m.metric_key === 'conversion_value') byDate[date].revenue += m.value || 0;
      if (m.metric_key === 'conversions') byDate[date].conversions += m.value || 0;
    });
    return Object.values(byDate)
      .map((d) => ({ ...d, roas: d.spend > 0 ? d.revenue / d.spend : null }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [measurements, validMeasurements]);

  const breakdown = useMemo(() => {
    if (!measurements) return [];
    const byEntity = {};
    validMeasurements.forEach((m) => {
      const key = m.entity_id || 'unknown';
      byEntity[key] = byEntity[key] || { entity_id: key, spend: 0, revenue: 0, conversions: 0, clicks: 0, impressions: 0 };
      if (m.metric_key === 'spend') byEntity[key].spend += m.value || 0;
      if (m.metric_key === 'conversion_value') byEntity[key].revenue += m.value || 0;
      if (m.metric_key === 'conversions') byEntity[key].conversions += m.value || 0;
      if (m.metric_key === 'clicks') byEntity[key].clicks += m.value || 0;
      if (m.metric_key === 'impressions') byEntity[key].impressions += m.value || 0;
    });
    return Object.values(byEntity).map((row) => ({
      ...row,
      name: entityNames[row.entity_id] || row.entity_id,
      roas: row.spend > 0 ? row.revenue / row.spend : null,
    })).sort((a, b) => b.spend - a.spend);
  }, [measurements, validMeasurements, entityNames]);

  const sortedRaw = useMemo(() => {
    if (!measurements) return [];
    const copy = [...measurements];
    copy.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av === bv) return 0;
      const cmp = (av ?? '') < (bv ?? '') ? -1 : 1;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [measurements, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  const loading = connectors === null || measurements === null;

  return (
    <DashboardLayout>
      <div data-testid="paid-media-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Paid Media</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Real, connector-synced Google Ads and Meta Ads performance — never a projected or example number.</p>
        </div>

        {!noClient && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {noClient && <EmptyState title="ADD A BRAND FIRST" description="Connect an advertising account once your workspace exists." />}

        {!noClient && loading && !error && <LoadingState />}
        {!noClient && error && <ErrorState error={error} onRetry={load} />}

        {!noClient && !error && !loading && !isConnected && (
          <EmptyState
            testId="paid-media-not-connected"
            title="Google Ads and Meta Ads are not connected"
            description="This page shows real campaign performance from Google Ads or Meta Ads. Google Analytics is a separate, later-phase connection and is not used here. Martian will never show an estimated spend, ROAS, or conversion number in place of real data."
            actionLabel="Go to Data Connections"
            onAction={() => navigate('/growth/data-connections')}
          />
        )}

        {!noClient && !error && !loading && isConnected && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 flex-wrap" data-testid="paid-media-connected-providers">
              {connectedProviders.map((c) => (
                <span key={c.id} className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)]">
                  {PROVIDER_LABELS[c.provider] || c.provider} <StatusBadge value={c.last_sync_status} />
                </span>
              ))}
            </div>

            {measurements.length === 0 ? (
              <EmptyState
                testId="paid-media-connected-no-data"
                title="Connected, no data yet"
                description="Your advertising account is connected but Martian has not synced any measurements yet."
                actionLabel="Go to Data Connections"
                onAction={() => navigate('/growth/data-connections')}
              />
            ) : (
              <>
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Summary</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="paid-media-summary">
                    {[
                      ['Spend', summary.spend, (v) => `${formatNumber(v, 2)}`],
                      ['Impressions', summary.impressions, (v) => formatNumber(v)],
                      ['Clicks', summary.clicks, (v) => formatNumber(v)],
                      ['CTR', summary.ctr, (v) => `${formatNumber(v, 2)}%`],
                      ['CPC', summary.cpc, (v) => formatNumber(v, 2)],
                      ['Conversions', summary.conversions, (v) => formatNumber(v)],
                      ['Revenue', summary.revenue, (v) => formatNumber(v, 2)],
                      ['ROAS', summary.roas, (v) => `${formatNumber(v, 2)}x`],
                    ].map(([label, value, fmt]) => (
                      <div key={label} data-testid={`paid-media-metric-${label.toLowerCase()}`} className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
                        <p className="text-[10px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-1.5">{label}</p>
                        {value === null || value === undefined ? (
                          <p className="text-[16px] font-semibold text-[var(--text-tertiary)]">—</p>
                        ) : (
                          <p className="text-[16px] font-semibold text-[var(--text-primary)]">{fmt(value)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {trend.length > 1 && (
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Trends</p>
                    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]" style={{ height: 260 }} data-testid="paid-media-trend-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="spend" stroke="var(--accent)" strokeWidth={2} dot={false} name="Spend" />
                          <Line type="monotone" dataKey="revenue" stroke="var(--success)" strokeWidth={2} dot={false} name="Revenue" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {breakdown.length > 0 && (
                  <div>
                    <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Breakdown by campaign</p>
                    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] overflow-x-auto" data-testid="paid-media-breakdown">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="border-b border-[var(--border)] text-left text-[var(--text-tertiary)]">
                            <th className="px-3 py-2">Campaign</th>
                            <th className="px-3 py-2">Spend</th>
                            <th className="px-3 py-2">Clicks</th>
                            <th className="px-3 py-2">Conversions</th>
                            <th className="px-3 py-2">ROAS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {breakdown.map((row) => (
                            <tr key={row.entity_id} className="border-b border-[var(--border-light,var(--border))] last:border-b-0">
                              <td className="px-3 py-2 text-[var(--text-primary)]">{row.name}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{formatNumber(row.spend, 2)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{formatNumber(row.clicks)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{formatNumber(row.conversions)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{row.roas === null ? '—' : `${formatNumber(row.roas, 2)}x`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Raw data</p>
                  <p className="text-[10px] text-[var(--text-tertiary)] mb-2">Click a row to see the full stored measurement record.</p>
                  <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] overflow-x-auto" data-testid="paid-media-raw-table">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="border-b border-[var(--border)] text-left text-[var(--text-tertiary)]">
                          {['observed_at', 'entity_id', 'metric_key', 'value', 'unit', 'source_reference'].map((col) => (
                            <th key={col} className="px-3 py-2 cursor-pointer select-none" onClick={() => toggleSort(col)} data-testid={`paid-media-sort-${col}`}>
                              {col}{sortKey === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                            </th>
                          ))}
                          <th className="px-3 py-2">status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedRaw.slice(0, 200).map((m) => (
                          <tr
                            key={m.id} data-testid={`paid-media-raw-row-${m.id}`}
                            className="border-b border-[var(--border-light,var(--border))] last:border-b-0 cursor-pointer hover:bg-[var(--surface-sunken,var(--canvas-secondary))] focus-within:bg-[var(--surface-sunken,var(--canvas-secondary))]"
                            onClick={() => setSelectedMeasurement(m)}
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter') setSelectedMeasurement(m); }}
                          >
                            <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{m.observed_at?.slice(0, 10)}</td>
                            <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{entityNames[m.entity_id] || m.entity_id}</td>
                            <td className="px-3 py-2 text-[var(--text-primary)] font-mono">{m.metric_key}</td>
                            <td className="px-3 py-2 text-[var(--text-primary)]">{formatNumber(m.value, 2)}</td>
                            <td className="px-3 py-2 text-[var(--text-tertiary)]">{m.unit}</td>
                            <td className="px-3 py-2 text-[var(--text-tertiary)] font-mono">{m.source_reference}</td>
                            <td className="px-3 py-2">{m.status ? <StatusBadge value={m.status} /> : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {sortedRaw.length > 200 && (
                    <p className="text-[10px] text-[var(--text-tertiary)] mt-1.5">Showing the first 200 of {sortedRaw.length} records.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Raw measurement detail ── */}
        <Sheet open={!!selectedMeasurement} onOpenChange={(o) => !o && setSelectedMeasurement(null)}>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="measurement-detail">
            {selectedMeasurement && (
              <>
                <SheetHeader>
                  <SheetTitle className="text-[var(--text-primary)] text-[14px]">{selectedMeasurement.metric_key}</SheetTitle>
                  <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">The real, stored measurement record.</SheetDescription>
                </SheetHeader>
                <div className="mt-3 space-y-3 text-[12px]">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Entity</p><p className="text-[var(--text-primary)]">{entityNames[selectedMeasurement.entity_id] || selectedMeasurement.entity_id}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Entity type</p><p className="text-[var(--text-primary)] font-mono">{selectedMeasurement.entity_type ?? '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Value</p><p className="text-[var(--text-primary)]">{formatNumber(selectedMeasurement.value, 2)} {selectedMeasurement.unit || ''}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Observed at</p><p className="text-[var(--text-primary)]">{selectedMeasurement.observed_at ?? '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Source type</p><p className="text-[var(--text-primary)] font-mono">{selectedMeasurement.source_type ?? '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Status</p><p>{selectedMeasurement.status ? <StatusBadge value={selectedMeasurement.status} /> : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Confidence</p><ConfidenceIndicator value={selectedMeasurement.confidence} /></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Baseline value</p><p className="text-[var(--text-primary)]">{selectedMeasurement.baseline_value === null || selectedMeasurement.baseline_value === undefined ? '—' : formatNumber(selectedMeasurement.baseline_value, 2)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Comparison value</p><p className="text-[var(--text-primary)]">{selectedMeasurement.comparison_value === null || selectedMeasurement.comparison_value === undefined ? '—' : formatNumber(selectedMeasurement.comparison_value, 2)}</p></div>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Source reference</p>
                    <p className="text-[var(--text-tertiary)] font-mono break-all">{selectedMeasurement.source_reference ?? '—'}</p>
                  </div>
                  {selectedMeasurement.metadata && Object.keys(selectedMeasurement.metadata).length > 0 && (
                    <>
                      <Separator className="bg-[var(--border)]" />
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1">Metadata</p>
                        <div className="space-y-1" data-testid="detail-measurement-metadata">
                          {Object.entries(selectedMeasurement.metadata).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-[11px] bg-[var(--surface-sunken,var(--canvas-secondary))] rounded px-2 py-1">
                              <span className="text-[var(--text-tertiary)] font-mono">{k}</span>
                              <span className="text-[var(--text-primary)] font-mono break-all">{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  <Separator className="bg-[var(--border)]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Record ID</p>
                    <p className="text-[var(--text-tertiary)] font-mono break-all">{selectedMeasurement.id ?? '—'}</p>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
